import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Server } from '../api/types.js';
import { useApiClient } from './use-api-client.js';
import { useToast } from './use-toast.js';

export function useStartServer() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serverId: string) => api.servers.start(serverId),
    onMutate: async (serverId) => {
      await queryClient.cancelQueries({ queryKey: ['servers'] });
      await queryClient.cancelQueries({ queryKey: ['server', serverId] });
      const previous = queryClient.getQueryData<Server[]>(['servers']);
      queryClient.setQueryData<Server[]>(['servers'], (old) =>
        old?.map((s) => (s.id === serverId ? { ...s, status: 'starting' } : s)),
      );
      queryClient.setQueryData<Server>(['server', serverId], (old) =>
        old ? { ...old, status: 'starting' } : old,
      );
      return { previous };
    },
    onError: (_err, serverId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['servers'], context.previous);
      }
      queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
    onSettled: (_data, _err, serverId) => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
  });
}

export function useStopServer() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serverId: string) => api.servers.stop(serverId, 'Manual stop (webapp)'),
    onMutate: async (serverId) => {
      await queryClient.cancelQueries({ queryKey: ['servers'] });
      await queryClient.cancelQueries({ queryKey: ['server', serverId] });
      const previous = queryClient.getQueryData<Server[]>(['servers']);
      queryClient.setQueryData<Server[]>(['servers'], (old) =>
        old?.map((s) => (s.id === serverId ? { ...s, status: 'stopping' } : s)),
      );
      queryClient.setQueryData<Server>(['server', serverId], (old) =>
        old ? { ...old, status: 'stopping' } : old,
      );
      return { previous };
    },
    onError: (_err, serverId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['servers'], context.previous);
      }
      queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
    onSettled: (_data, _err, serverId) => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
  });
}

export function useReloadSecrets() {
  const api = useApiClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (serverId: string) => api.servers.reloadSecrets(serverId),
    onSuccess: (data) => {
      toast(`Secrets reloaded (${data.secrets.total} keys)`, 'success');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to reload secrets', 'error');
    },
  });
}

export function useSyncSchedules() {
  const api = useApiClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (serverId: string) => api.servers.syncSchedules(serverId),
    onSuccess: (data) => {
      const parts = [];
      if (data.created.length) parts.push(`${data.created.length} created`);
      if (data.updated.length) parts.push(`${data.updated.length} updated`);
      if (data.deleted.length) parts.push(`${data.deleted.length} deleted`);
      toast(parts.length ? `Schedules synced: ${parts.join(', ')}` : 'Schedules in sync', 'success');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to sync schedules', 'error');
    },
  });
}
